const stateKeys = {
  interval: 'forecast-interval'
}

export const actions = {
  clearStorage() {
    for (const prop in stateKeys) {
      this.$storage.removeUniversal(stateKeys[prop])
    }
  },

  async getData({ dispatch }, { format, interval }) {
    const data = await this.$apiAaltra.forecast(
      format._format,
      interval._period
    )

    const forecastHistory = await this.$apiAaltra.forecastHistory(
      format._format,
      interval._period
    )

    const showBudgetRelatedData = this.$storage.getUniversal(
      'showBudgetRelatedData'
    )

    const energyKind = this.$getEnergyKind(format.text)
    const selfConsumptionRate = data[energyKind].selfConsumptionRate
    const currentData = data[energyKind].current
    const exportedData = data[energyKind].exported
    const importedData = data[energyKind].imported
    const forecastedData = data[energyKind].forecasted
    const expectedData = data[energyKind].expected

    forecastHistory.data = forecastHistory.data.map((d) => {
      return {
        data: this.$getEnergyComparison(d[energyKind], format.text),
        title: this.$toLongDate(this.$epochToDateTime(d.timestamp), true),
        comparison: d[energyKind].percentile
      }
    })

    let lastKnownForecastedIndex = data.timestamps
      ? data.timestamps.length - 1
      : null
    if (forecastedData.data != null && lastKnownForecastedIndex) {
      lastKnownForecastedIndex = forecastedData.data.length - 1
      while (forecastedData.data[lastKnownForecastedIndex] === null) {
        lastKnownForecastedIndex--
      }
      if (lastKnownForecastedIndex === -1) {
        lastKnownForecastedIndex = data.timestamps.length - 1
      }
    }

    const lastValueTimestamp = data[energyKind].lastValueTimestamp
    const lastValueDate = lastValueTimestamp
      ? this.$formatEpochToDate(lastValueTimestamp)
      : ''
    const lastValueIndex = data.timestamps.indexOf(lastValueTimestamp)

    const currentLegendLabel = this.app.i18n.t(`${format.text}_current`)
    const selfConsumptionRateLegendLabel = this.app.i18n.t(
      'selfConsumptionRate'
    )
    const forecastedLegendLabel = this.app.i18n.t(`${format.text}_forecasted`)
    const expectedLegendLabel = this.app.i18n.t(`${format.text}_expected`)

    const unit =
      format._format === 'energy'
        ? this.app.i18n.t('kwh')
        : this.$currency.get()

    const chartData = await dispatch('mapToChartData', {
      timestamps: data.timestamps,
      data: data[energyKind],
      format,
      interval
    })

    const forecastChartDataEmpty = await dispatch(
      'energy/home/checkIfDatasetsDataAreEmpty',
      chartData,
      { root: true }
    )

    const lastDayOfMonth = data.timestamps[data.timestamps.length - 1]
    const chartLegends = []
    if (
      lastValueIndex &&
      expectedData &&
      expectedData.data &&
      expectedData.data[lastValueIndex] &&
      showBudgetRelatedData
    ) {
      chartLegends.push({
        chartLegend: {
          date: lastValueDate,
          text: expectedLegendLabel,
          type: `expected-${format.text}`.replace('-grid', '')
        },
        values: this.$formatUnit(
          this.$formatEnergy(expectedData.data[lastValueIndex] || 0),
          unit
        )
      })
    }
    if (format.text === 'solar' && importedData && exportedData) {
      if (exportedData.total) {
        const dataStyle = this.$chartDatasets('solar-export', 'bar')
        chartLegends.push({
          chartLegend: {
            date: lastValueDate,
            text: dataStyle.label,
            type: dataStyle.legend
          },
          values: this.$formatUnit(
            this.$formatEnergy(exportedData.total || 0),
            unit
          )
        })
      }
      if (importedData.total) {
        const dataStyle = this.$chartDatasets('solar', 'bar')
        chartLegends.push({
          chartLegend: {
            date: lastValueDate,
            text: dataStyle.label,
            type: dataStyle.legend
          },
          values: this.$formatUnit(
            this.$formatEnergy(importedData.total || 0),
            unit
          )
        })
      }
      if (selfConsumptionRate) {
        chartLegends.push({
          chartLegend: {
            date: lastValueDate,
            text: selfConsumptionRateLegendLabel
          },
          values: `${this.$formatEnergy(selfConsumptionRate || 0)}%`
        })
      }
    } else if (currentData.total) {
      chartLegends.push({
        chartLegend: {
          date: lastValueDate,
          text: currentLegendLabel,
          type: `forecast-${format.text}`.replace('-grid', '')
        },
        values: this.$formatUnit(
          this.$formatEnergy(currentData.total || 0),
          unit
        )
      })
    }
    if (expectedData.total && showBudgetRelatedData) {
      chartLegends.push({
        chartLegend: {
          date: this.$formatEpochToDate(
            lastDayOfMonth,
            interval._period === 'month'
          ),
          period: interval._period,
          text: expectedLegendLabel,
          type: `expected-${format.text}`.replace('-grid', '')
        },
        values: this.$formatUnit(
          this.$formatEnergy(expectedData.total || 0),
          unit
        )
      })
    }
    if (forecastedData.total) {
      chartLegends.push({
        chartLegend: {
          date: this.$formatEpochToDate(
            lastDayOfMonth,
            interval._period === 'month'
          ),
          period: interval._period,
          text: forecastedLegendLabel,
          type: `forecasted-${format.text}`.replace('-grid', '')
        },
        values: this.$formatUnit(
          this.$formatEnergy(forecastedData.total || 0),
          unit
        )
      })
    }

    return {
      empty: forecastChartDataEmpty,
      endOfYearAvailable: data.endOfYearAvailable,
      history:
        forecastHistory.data && forecastHistory.data.length > 0
          ? forecastHistory
          : null,
      today: lastValueDate,
      percentile:
        data[energyKind] && data[energyKind].percentile
          ? data[energyKind].percentile
          : null,
      savings:
        data[energyKind] && data[energyKind].savings
          ? data[energyKind].savings
          : null,
      chartData,
      energy: {
        data: chartLegends
      }
    }
  },

  getInterval() {
    return (
      this.$storage.getUniversal(stateKeys.interval) ||
      this.$tabItems('forecast')[0]
    )
  },

  setInterval(_, value) {
    this.$storage.setUniversal(stateKeys.interval, value)
  },

  mapToChartData(_, { timestamps, data, format, interval }) {
    const unit =
      format._format === 'energy'
        ? this.app.i18n.t('kwh')
        : this.$currency.get()
    const currentData = data.current
    const importedData = data.imported
    const exportedData = data.exported
    const forecastedData = data.forecasted
    const expectedData = data.expected
    const zeroData = []
    const labels = timestamps
      ? timestamps.map((timestamp) => {
          zeroData.push('0')
          return interval._period === 'month'
            ? this.$formatEpochToDayOfMonth(timestamp)
            : this.$formatEpochToMonth(timestamp)
        }) || []
      : []

    const datastyles = []
    const datasets = []

    const lastValueTimestamp = data.lastValueTimestamp
    const lastValueIndex = timestamps
      ? timestamps.indexOf(lastValueTimestamp)
      : null

    const showBudgetRelatedData = this.$storage.getUniversal(
      'showBudgetRelatedData'
    )

    // Reversed graph order (because it is also reversed in CustomTooltip)
    if (expectedData.data && showBudgetRelatedData) {
      datastyles.push(
        this.$chartDatasets(
          `expected-${format.text}`.replace('-grid', ''),
          'line'
        )
      )
      datasets.push({
        data: expectedData.data?.map((x) => x || null)
      })
    }
    if (forecastedData.data) {
      datastyles.push(
        this.$chartDatasets(
          `forecasted-${format.text}`.replace('-grid', ''),
          'bar'
        )
      )
      datasets.push({
        data: forecastedData.data?.map((x) => x || null)
      })
    }
    if (format.text === 'solar' && importedData && exportedData) {
      if (importedData.data) {
        // Hide for today
        importedData.data[lastValueIndex] = null
        datastyles.push(this.$chartDatasets('solar', 'bar'))
        datasets.push({
          data: importedData.data?.map((x) => x || null)
        })
      }
      if (exportedData.data) {
        // Hide for today
        exportedData.data[lastValueIndex] = null
        datastyles.push(this.$chartDatasets('solar-export', 'bar'))
        datasets.push({
          data: exportedData.data?.map((x) => x || null)
        })
      }
    } else if (currentData.data) {
      datastyles.push(
        this.$chartDatasets(
          `forecast-${format.text}`.replace('-grid', ''),
          'bar'
        )
      )
      datasets.push({
        data: currentData.data?.map((x) => x || null)
      })
    }

    const result = {
      labels,
      datasets,
      datastyles,
      unit
    }

    return result
  }
}
